import PropTypes from 'prop-types';
import { useRef } from 'react';
import * as subscriptions from '@sendpayments/graphql/subscriptions';
import * as queries from '@sendpayments/graphql/queries';
import { subscribe, query } from '@sendpayments/js-utils';
import { buildUniqueEntityKeys } from '@sendpayments/js-utils/dist/services/build-unique-key';
import { logger } from '@sendpayments/js-utils';
import { message } from 'antd';
import { useSubscription } from '../hooks';

// for now we will fix to this types, eventually the realtime websocket will know that data to send to the different users.
const CoconutType = {
  Retail: 'Retail',
  Beneficiary: 'Beneficiary',
  Corporate: 'Corporate',
  Document: 'Document',
  Setting: 'Setting',
  Payment: 'Payment',
  Conversion: 'Conversion',
};

const onCreateCoconutByOwnerTypeSubscription = (owner, type) => {
  if (owner && type) {
    return subscribe(subscriptions.onCreateCoconutByOwnerTypeV2, { params: { owner, type } });
  }
  return undefined;
};

const onUpdateCoconutByOwnerTypeSubscription = (owner, type) => {
  if (owner && type) {
    return subscribe(subscriptions.onUpdateCoconutByOwnerTypeV2, { params: { owner, type } });
  }
  return undefined;
};

const RemoteDataSubscriptionUpdater = ({ owner, onUpdateEntity, onCreateEntity, onError }) => {
  const remoteDataSubscriptions = useRef([]);

  const onUpdateSubscription = async (result) => {
    try {
      const { id, type } = result;
      const entity = await query(queries.getCoconut, { params: { id, type } });
      onUpdateEntity(entity);
    } catch {
      message.error('Some connection error is happening');
    }
  };

  const onCreateSubscription = async (result) => {
    try {
      const { id, type } = result;
      const entity = await query(queries.getCoconut, { params: { id, type } });
      onCreateEntity(entity, {
        keys: buildUniqueEntityKeys(entity),
      });
      onUpdateEntity(entity);
    } catch {
      message.error('Some connection error is happening');
    }
  };

  const onSubscriptionError = (err) => {
    // we have to trigger the on error only once.
    if (remoteDataSubscriptions.current?.length > 0) {
      const subs = remoteDataSubscriptions.current;
      remoteDataSubscriptions.current = [];
      logger.log('@sendpayments/react-shared/remote-data', 'reconnecting remote data subscriptions');

      logger.error('@sendpayments/react-shared/remote-data', 'error', err);
      // reconnect in 10 seconds
      setTimeout(() => {
        subs.forEach(([{ reconnect }]) => reconnect());
        onError(err);
      }, 10000);
    }

    logger.log('@sendpayments/react-shared/remote-data', 'skipping cascade error from subscriptions');
  };

  remoteDataSubscriptions.current = [
    // Conversion
    useSubscription(
      () => ({
        subscriber: () => onCreateCoconutByOwnerTypeSubscription(owner, CoconutType.Conversion),
        onDataReceived: onCreateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Conversion],
    ),
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription(owner, CoconutType.Conversion),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Conversion],
    ),

    // Payment
    useSubscription(
      () => ({
        subscriber: () => onCreateCoconutByOwnerTypeSubscription(owner, CoconutType.Payment),
        onDataReceived: onCreateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Payment],
    ),
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription(owner, CoconutType.Payment),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Payment],
    ),

    // Beneficiary
    useSubscription(
      () => ({
        subscriber: () => onCreateCoconutByOwnerTypeSubscription(owner, CoconutType.Beneficiary),
        onDataReceived: onCreateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Beneficiary],
    ),
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription(owner, CoconutType.Beneficiary),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Beneficiary],
    ),

    // Retail
    useSubscription(
      () => ({
        subscriber: () => onCreateCoconutByOwnerTypeSubscription(owner, CoconutType.Retail),
        onDataReceived: onCreateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Retail],
    ),
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription(owner, CoconutType.Retail),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Retail],
    ),

    // Corporate
    useSubscription(
      () => ({
        subscriber: () => onCreateCoconutByOwnerTypeSubscription(owner, CoconutType.Corporate),
        onDataReceived: onCreateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Corporate],
    ),
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription(owner, CoconutType.Corporate),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Corporate],
    ),

    // Document
    useSubscription(
      () => ({
        subscriber: () => onCreateCoconutByOwnerTypeSubscription(owner, CoconutType.Document),
        onDataReceived: onCreateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Document],
    ),
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription(owner, CoconutType.Document),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      [owner, CoconutType.Document],
    ),

    // CustomerNotification
    // not needed ro now we are polling for the data

    // Setting
    useSubscription(
      () => ({
        subscriber: () => onUpdateCoconutByOwnerTypeSubscription('admin@snedfx.com.au', CoconutType.Setting),
        onDataReceived: onUpdateSubscription,
        onError: onSubscriptionError,
      }),
      ['admin@snedfx.com.au', CoconutType.Setting],
    ),
  ];

  return null;
};

RemoteDataSubscriptionUpdater.propTypes = {
  owner: PropTypes.string.isRequired,
  onUpdateEntity: PropTypes.func.isRequired,
  onCreateEntity: PropTypes.func.isRequired,
};

export default RemoteDataSubscriptionUpdater;
